import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { notifyError, notifySuccess } from '@/utils/notify';

export interface TermsParams {
    id: string;
    versao_atual: number;
    url_termos_uso: string | null;
    url_politica_privacidade: string | null;
    url_tratamento_dados: string | null;
    url_regras_institucionais: string | null;
    obrigatorio_desde: string | null;
    ativo: boolean;
}

export type GatekeeperStatus = 'loading' | 'allowed' | 'bypass' | 'blocked_soft' | 'blocked_hard' | 'error';

export const useTermsGatekeeper = () => {
    const { user, profile } = useAuth();
    const queryClient = useQueryClient();

    const fetchGatekeeperStatus = async () => {
        if (!user || (!profile?.tenant_id && !user.app_metadata?.tenant_id)) {
            return { status: 'loading' as GatekeeperStatus, params: null };
        }

        // 1. Chamar RPC atualizada para verificar status de permissão unificada
        const { data: hasAccess, error: rpcError } = await supabase.rpc('fn_usuario_pode_acessar', {
            p_user_id: user.id,
        });

        if (rpcError) {
            console.error('Erro ao verificar acesso:', rpcError);
            return { status: 'error' as GatekeeperStatus, params: null };
        }

        const tenantId = profile?.tenant_id || user.app_metadata?.tenant_id;

        // Precisamos dos parâmetros de termos para exibir no UI (URLs, etc) caso o usuário NÃO tenha acesso ou caso ele TENHA mas haja um aviso prévio.
        const { data: termParams, error: paramsError } = await supabase
            .from('parametros_termos')
            .select('id, versao_atual, url_termos_uso, url_politica_privacidade, url_tratamento_dados, url_regras_institucionais, obrigatorio_desde, ativo')
            .eq('tenant_id', tenantId)
            .eq('ativo', true)
            .order('versao_atual', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (paramsError) {
            console.error('Erro ao buscar parametros de termos:', paramsError);
            return { status: 'error' as GatekeeperStatus, params: null };
        }

        // Se `hasAccess` retornou TRUE, pode ser: Bypass, Aviso Prévio ou Conformidade.
        if (hasAccess) {
            if (!termParams) {
                return { status: 'bypass' as GatekeeperStatus, params: null };
            }

            // Se há um termo e ele tem data futura, estamos na regra de AVISO PRÉVIO.
            if (termParams.obrigatorio_desde) {
                const obrigatorioDesde = new Date(termParams.obrigatorio_desde);
                const now = new Date();

                // O usuário no banco possui todas as flags TRUE E também passou do prazo?
                // Ou ele só passou pq está antes do prazo?
                // Na vida real, o "hasAccess" cobriu as duas coisas. 
                // Vamos verificar explicitamente o DB profile state para ver se ele de fato aceitou ou está só no aviso prévio.

                const { data: profileCheck } = await supabase
                    .from('usuarios_perfis')
                    .select('versao_termos_aceita, aceite_termos_uso')
                    .eq('id', user.id)
                    .single();

                const acceptedEverything = profileCheck?.versao_termos_aceita >= termParams.versao_atual
                    && profileCheck?.aceite_termos_uso === true;

                if (now < obrigatorioDesde && !acceptedEverything) {
                    return { status: 'blocked_soft' as GatekeeperStatus, params: termParams };
                }
            }

            return { status: 'allowed' as GatekeeperStatus, params: null };
        }

        // Se `hasAccess` é FALSE, então: Tem termo ativo, Já passou/é o dia do prazo, e o usuário não aceitou tudo.
        return { status: 'blocked_hard' as GatekeeperStatus, params: termParams };
    };

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['terms-gatekeeper', user?.id],
        queryFn: fetchGatekeeperStatus,
        enabled: !!user,
        // Termos mudam raramente — evita refetch a cada navegação/focus.
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const acceptTermsMutation = useMutation({
        mutationFn: async ({ versao, ip, userAgent }: { versao: number; ip: string; userAgent: string }) => {
            if (!user) throw new Error('Usuário não autenticado');

            const logEntry = {
                data: new Date().toISOString(),
                versao,
                ip,
                ua: userAgent,
            };

            // O supabase não tem array_append nativo no update (exceto por RPC), 
            // então primeiro buscamos o histórico existente.
            const { data: currentProfile, error: profileError } = await supabase
                .from('usuarios_perfis')
                .select('historico_aceites')
                .eq('id', user.id)
                .single();

            if (profileError) throw profileError;

            const currentHistory = Array.isArray(currentProfile?.historico_aceites)
                ? currentProfile.historico_aceites
                : [];

            const { error: updateError } = await supabase
                .from('usuarios_perfis')
                .update({
                    aceite_termos_uso: true,
                    aceite_politica_privacidade: true,
                    aceite_tratamento_dados: true,
                    aceite_regras_instituicao: true,
                    versao_termos_aceita: versao,
                    historico_aceites: [...currentHistory, logEntry],
                })
                .eq('id', user.id);

            if (updateError) throw updateError;

            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['terms-gatekeeper', user?.id] });
            notifySuccess('Pronto!', 'Termos e políticas aceitos com sucesso.');
        },
        onError: (error) => {
            console.error('Erro ao aceitar os termos:', error);
            notifyError('Erro!', 'Ocorreu um erro ao salvar o aceite. Tente novamente.');
        }
    });

    return {
        status: isLoading ? 'loading' : data?.status ?? 'loading',
        params: data?.params as TermsParams | null,
        acceptTerms: acceptTermsMutation.mutateAsync,
        isAccepting: acceptTermsMutation.isPending,
        refetch,
    };
};
